import { ButtonHTMLAttributes, forwardRef, AnchorHTMLAttributes } from 'react';
import { TIconButtonSize } from '@legacy/assets/variables/button';
import { TIconSizes } from '@legacy/assets/variables/size';
import { TIconName } from '@legacy/assets/variables/icon';
import { TStatus } from '@legacy/assets/variables/status';
import { Icon } from '@legacy/components/ui-components/Icon';
import { Avatar } from '@legacy/components/ui-components/Avatar';
import { Count } from '@legacy/components/ui-components/Count';
import {
  IconButtonBase,
  IconButtonBaseSharedProps,
  IconButtonAnchorBaseSharedProps,
} from './IconButtonBase';
import styles from './IconButton.module.scss';

export interface IconButtonProps
  extends Omit<IconButtonBaseSharedProps, 'as' | 'children'>,
    Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Size of the icon */
  iconSize?: TIconButtonSize;
  tone?: TStatus | 'light' | 'dark';
  iconName: TIconName;
  count?: number;
  countTone?: TStatus;
}

type IconButtonRef = HTMLButtonElement;
export const IconButtonSizeMapping: Record<TIconButtonSize, TIconSizes> = {
  tiny: 'small',
  compact: 'small',
  default: 'normal',
  large: 'large',
};

export const IconButton = forwardRef<IconButtonRef, IconButtonProps>(
  (
    { iconSize = 'default', iconName, tone, count, countTone, ...props },
    ref,
  ) => (
    <IconButtonBase
      as="button"
      type="button"
      ref={ref}
      size={iconSize}
      {...props}
    >
      <Icon
        iconName={iconName}
        size={IconButtonSizeMapping[iconSize]}
        tone={tone}
        aria-hidden="true"
      />

      {count && count > 0 && (
        <Count className={styles.count} count={count} tone={countTone} />
      )}
    </IconButtonBase>
  ),
);

export interface AvatarButtonProps
  extends Omit<IconButtonProps, 'icon' | 'iconName' | 'tone'> {
  avatar: string;
}

export const AvatarButton = forwardRef<IconButtonRef, AvatarButtonProps>(
  ({ avatar, iconSize = 'default', ...props }, ref) => (
    <IconButtonBase
      as="button"
      type="button"
      ref={ref}
      size={iconSize}
      {...props}
    >
      <Avatar src={avatar} size={IconButtonSizeMapping[iconSize]} alt="" />
    </IconButtonBase>
  ),
);

// Component for anchor link
export interface IconButtonAnchorProps
  extends Omit<IconButtonAnchorBaseSharedProps, 'as' | 'children'>,
    Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'children' | 'href'> {
  /** Size of the icon */
  iconSize?: TIconButtonSize;
  tone?: TStatus;
  iconName: TIconName;
  count?: number;
  countTone?: TStatus;
}

type IconButtonAnchorRef = HTMLAnchorElement;

export const IconButtonAnchor = forwardRef<
  IconButtonAnchorRef,
  IconButtonAnchorProps
>(
  (
    { iconSize = 'default', iconName, tone, count, countTone, ...props },
    ref,
  ) => (
    <IconButtonBase as="a" ref={ref} size={iconSize} {...props}>
      <Icon
        iconName={iconName}
        size={IconButtonSizeMapping[iconSize]}
        tone={tone}
        aria-hidden="true"
      />

      {count && count > 0 && (
        <Count className={styles.count} count={count} tone={countTone} />
      )}
    </IconButtonBase>
  ),
);

export interface AvatarButtonAnchorProps
  extends Omit<IconButtonAnchorProps, 'icon' | 'iconName' | 'tone'> {
  avatar: string;
}

export const AvatarButtonAnchor = forwardRef<
  IconButtonAnchorRef,
  AvatarButtonAnchorProps
>(({ avatar, iconSize = 'default', ...props }, ref) => (
  <IconButtonBase as="a" ref={ref} size={iconSize} {...props}>
    <Avatar src={avatar} size={IconButtonSizeMapping[iconSize]} alt="" />
  </IconButtonBase>
));
